// GroundOps marketing landing page.
// Visual system: cream + dashed-grid + terracotta accent. Animations are
// driven from a single useLayoutEffect (scroll-in, stat count-up, hero stats).
//
// NOTE: Testimonials are PLACEHOLDER content (labelled as illustrative
// samples on the page) — swap in real, attributable quotes as pilots wrap.
// Pricing deliberately shows an early-access panel, not dollar tiers.
// Copy rule: describe only shipped capability — see CLAUDE.md → Marketing site.
import { useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import './landing.css'

// Same convention as the field app: staging is the serving backend (#334).
// Fallback is STAGING deliberately — the deployed site is built with
// VITE_API_URL=https://api.groundops.app (deploy-www.yml), so this value is
// only ever used by someone running www locally, and a test demo request
// should not become a real lead. Until 2026-08-21 the workflow set nothing and
// the LIVE site ran on this fallback, posting real prospects into staging.
const API_BASE = import.meta.env.VITE_API_URL ?? 'https://stg.api.groundops.app'

/* ── Shared icons ──────────────────────────────────────────── */
// GroundOps brand "G" logomark (from src/www/public/favicon-*.svg)
function LogoMark({ size = 18, color = 'white' }: { size?: number; color?: string }) {
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} fill="none" aria-hidden>
      <path d="M17.372 2C14.9129 2 12.495 2.61462 10.3495 3.78506C8.20402 4.9555 6.4027 6.64261 5.11785 8.68502C3.83301 10.7274 3.10762 13.0568 3.01108 15.4504C2.91454 17.8439 3.45007 20.2216 4.56645 22.3559C5.68284 24.4902 7.34272 26.3098 9.38734 27.6406C11.432 28.9714 13.7929 29.7689 16.2444 29.9568C18.6958 30.1448 21.1558 29.7169 23.389 28.714C25.6221 27.7111 27.5538 26.1669 28.9992 24.229L23.4707 20.3163C22.7126 21.3328 21.6994 22.1428 20.528 22.6688C19.3567 23.1948 18.0664 23.4193 16.7805 23.3207C15.4947 23.2221 14.2563 22.8038 13.1838 22.1058C12.1114 21.4077 11.2407 20.4533 10.6552 19.3338C10.0696 18.2143 9.78871 16.9672 9.83935 15.7117C9.88998 14.4562 10.2705 13.2344 10.9444 12.1631C11.6183 11.0918 12.5632 10.2069 13.6885 9.59297C14.8139 8.97904 16.0822 8.65666 17.372 8.65666V2Z" fill={color} />
      <path d="M29 18.2791C28.186 18.3956 27.3919 18.6285 26.6443 18.9734C25.6914 19.4129 24.8347 20.0242 24.1176 20.7727L19.214 17.3018C20.5424 15.7634 22.1946 14.52 24.0611 13.6591C25.6175 12.9412 27.2906 12.5031 29 12.3622V18.2791Z" fill={color} />
    </svg>
  )
}

function CornerBracket() {
  return (
    <svg viewBox="0 0 71 71" width="22" height="22" aria-hidden>
      <path
        d="M71 8L69 8L69 2L63 2L63 0L71 0Z M63 71L63 69L69 69L69 63L71 63L71 71Z M0 63L2 63L2 69L8 69L8 71L0 71Z M8 0L8 2L2 2L2 8L0 8L0 0Z"
        fill="var(--accent)"
      />
    </svg>
  )
}

/* ── Content data ──────────────────────────────────────────── */
const CAPABILITIES = [
  { n: '01', title: 'Guided photo capture', body: 'Step-by-step checklists make sure crews capture every required angle — crush rock, pit tag, mandrel, breakouts. Nothing gets missed.' },
  { n: '02', title: 'Office photo review', body: 'Every submission lands in a review queue — the office approves each photo or flags a defect, and the feedback goes straight back to the crew\u2019s phone.' },
  { n: '03', title: 'GPS & timestamp', body: 'Every photo is tagged with location and time the moment it is taken — tamper-proof evidence for compliance audits.' },
  { n: '04', title: 'Crew management', body: 'Assign crews to projects, track progress across spans and see real-time completion status from the office.' },
  { n: '05', title: 'Organised evidence', body: 'Approved photos are filed per job with GPS and timestamps, and push straight to your Google Drive — ready to hand to the client.' },
  { n: '06', title: 'Works offline', body: 'Capture in the field with no signal. Everything syncs automatically the moment the crew is back in range.' },
]

const PROCESS = [
  { n: '.01', title: 'Start your job', label: 'Open the app to your crew\u2019s job list — pick up an assigned job or create one right there at the site.' },
  { n: '.02', title: 'Follow the photo checklist', label: 'For each pit or trench the app walks the crew through every required photo: crush rock, pit tag, mandrel, breakouts, internal, gasket, lid, overview.' },
  { n: '.03', title: 'The office reviews every photo', label: 'Submissions land in the office review queue. Each photo is approved or flagged with a defect reason that goes straight back to the crew\u2019s phone.' },
  { n: '.04', title: 'Sync & share', label: 'Photos sync to the cloud with GPS and timestamps, filed neatly per job — push approved evidence to Google Drive to share.' },
]

const STATS = [
  { target: 100, suffix: '%', label: 'of photos GPS-tagged\nand timestamped' },
  { target: 8, suffix: '', label: 'guided angles\nper pit checklist' },
  { target: 0, suffix: '', label: 'signal needed\nto capture on site' },
]

const WORK_TYPES = [
  { tone: 'dark' as const, title: 'Pit & pole installations', desc: 'Telecommunications and utility pits documented to spec — crush rock through to lid and overview, every span.' },
  { tone: 'light' as const, title: 'Trenching & conduit', desc: 'Capture depth, bedding and reinstatement evidence as the work happens, without slowing the crew down.' },
  { tone: 'dark' as const, title: 'Asset inspections', desc: 'Routine condition checks and defect reporting with GPS-stamped photographic proof, ready for the asset owner.' },
]

const INDUSTRIES = [
  { eyebrow: '01', title: 'Telecommunications', body: 'Fibre and pit/pole rollouts documented to carrier standards.' },
  { eyebrow: '02', title: 'Utilities', body: 'Water, power and gas asset works with audit-ready evidence.' },
  { eyebrow: '03', title: 'Civil contractors', body: 'Trenching, conduit and reinstatement across every span.' },
  { eyebrow: '04', title: 'Asset owners', body: 'Condition inspections and defect tracking on your network.' },
  { eyebrow: '05', title: 'Local government', body: 'Infrastructure maintenance with a defensible photo trail.' },
  { eyebrow: '06', title: 'Inspection services', body: 'Third-party verification with consistent, structured capture.' },
]

const HUB_NODES = ['Google Drive', 'Slack', 'Email', 'Photo storage', 'Cloud sync', 'GPS', 'iOS app', 'Web app']

const WHY = [
  { n: '01', title: 'Field-first design', label: 'Big touch targets for gloved hands and a high-contrast UI you can read in direct sun.' },
  { n: '02', title: 'Offline-first', label: 'Capture without signal and sync automatically the moment you are back in range.' },
  { n: '03', title: 'Built-in review loop', label: 'Defects are flagged while the crew is still nearby — feedback lands on their phone, not in next week\u2019s paperwork.' },
  { n: '04', title: 'Audit-ready', label: 'GPS, timestamps and structured checklists make every submission defensible.' },
]

// PLACEHOLDER — replace with real, attributable customer quotes before launch.
const TESTIMONIALS = [
  { name: 'Sample quote', role: 'Site supervisor · Civil contractor', quote: 'Crews capture the right photos the first time, so the office stops chasing reshoots. Reporting that used to take an afternoon is done before they leave site.' },
  { name: 'Sample quote', role: 'Operations manager · Utilities', quote: 'Every job comes back GPS-stamped and complete. When a client asks for evidence, it is already there — no scrambling through camera rolls.' },
  { name: 'Sample quote', role: 'Project lead · Telecommunications', quote: 'Onboarding a new crew takes minutes. The checklist guides them, and the office catches anything that is off before it becomes a problem.' },
]

const FAQ = [
  { q: 'What kind of crews is GroundOps built for?', a: 'Civil construction and infrastructure crews — telecommunications pit/pole, utilities, trenching and conduit, and asset inspections. Anywhere photo evidence and compliance reporting matter.' },
  { q: 'Do crews need technical training to use it?', a: 'No. The app guides crews through a photo checklist step by step. If someone can take a photo on a phone, they can document a job correctly.' },
  { q: 'Does it work without cell signal?', a: 'Yes. GroundOps is offline-first. Crews capture everything in the field with no signal, and it syncs automatically once they are back in range.' },
  { q: 'How are photos verified?', a: 'Every submission goes to an office review queue. Each photo is approved or flagged with a defect reason, and that feedback lands back on the crew\u2019s phone — so problems get fixed while the crew is still on site.' },
  { q: 'How do we hand evidence to clients and auditors?', a: 'Every photo carries GPS coordinates, a timestamp and its checklist slot, filed per job. Push approved evidence straight to Google Drive to share — one-click PDF reports are on the roadmap.' },
  { q: 'How do we get started?', a: 'Head to groundops.app/signup — free while GroundOps is in early access, no credit card required. We\u2019ll get your organisation set up; then you invite your crew, and they sign in to the field app with their mobile number. Prefer a guided walkthrough first? Book a demo below.' },
]

/* ── Component ─────────────────────────────────────────────── */
export default function Landing() {
  const rootRef = useRef<HTMLDivElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [workIdx, setWorkIdx] = useState(0)
  const [testiIdx, setTestiIdx] = useState(0)
  const [faqOpen, setFaqOpen] = useState<number | null>(0)
  const [demoEmail, setDemoEmail] = useState('')
  const [demoName, setDemoName] = useState('')
  const [demoCompany, setDemoCompany] = useState('')
  const [demoTrap, setDemoTrap] = useState('') // honeypot — humans never see it
  const [demoStatus, setDemoStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  // Server-supplied error text (e.g. the API's 400 "A valid email address is
  // required"); null = show the generic message + mailto fallback.
  const [demoError, setDemoError] = useState<string | null>(null)

  // Same shape the API enforces — catching a typo'd address here keeps a bad
  // email from reading as a site failure (the old flow rendered the 400 as
  // "Something went wrong", which looked like an outage and cost the lead).
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  async function submitDemoRequest() {
    const email = demoEmail.trim()
    if (!email || demoStatus === 'sending') return
    if (!EMAIL_RE.test(email)) {
      setDemoError('That email address doesn’t look right — check it and try again.')
      setDemoStatus('error')
      return
    }
    setDemoStatus('sending')
    setDemoError(null)
    try {
      const res = await fetch(`${API_BASE}/api/public/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name: demoName.trim() || undefined,
          company: demoCompany.trim() || undefined,
          website: demoTrap,
        }),
      })
      if (!res.ok) {
        // Surface the API's own message when it has one (validation, rate
        // limit) — only unknown failures fall back to the mailto line.
        const body = (await res.json().catch(() => null)) as { error?: string } | null
        setDemoError(body?.error ?? null)
        setDemoStatus('error')
        return
      }
      setDemoStatus('sent')
    } catch {
      setDemoError(null)
      setDemoStatus('error')
    }
  }

  useLayoutEffect(() => {
    const root = rootRef.current
    if (!root) return

    const animSelectors = [
      '.section-title', '.grid-cell', '.process-step', '.stat-num', '.stat-label',
      '.case-name', '.case-desc', '.solution-card', '.why-card', '.testi-name',
      '.testi-quote', '.pricing-tier', '.faq-item', '.hub-node', '.footer-big-text',
    ].join(',')

    const scrollObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            // Attribute, NOT a class: React owns className and rewrites it on
            // re-render, which wiped the reveal class from any animated element
            // with a dynamic class — clicking a FAQ item made it vanish.
            e.target.setAttribute('data-visible', '')
            scrollObs.unobserve(e.target)
          }
        })
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' },
    )

    const animEls = Array.from(root.querySelectorAll<HTMLElement>(animSelectors))
    animEls.forEach((el, i) => {
      el.setAttribute('data-animate', '')
      // stagger within visual groups of ~6
      if (!el.classList.contains('section-title')) {
        el.style.animationDelay = `${(i % 6) * 0.08}s`
      }
      scrollObs.observe(el)
    })

    // Stat count-up
    const countUp = (el: HTMLElement) => {
      const target = parseFloat(el.dataset.target || '0')
      const suffix = el.dataset.suffix || ''
      const duration = 1600
      const start = performance.now()
      const tick = (now: number) => {
        const pct = Math.min((now - start) / duration, 1)
        const eased = 1 - Math.pow(1 - pct, 3)
        el.textContent = Math.round(eased * target) + suffix
        if (pct < 1) requestAnimationFrame(tick)
      }
      requestAnimationFrame(tick)
    }
    const statObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const el = e.target as HTMLElement
          if (e.isIntersecting && !el.dataset.done) {
            el.dataset.done = '1'
            countUp(el)
          }
        })
      },
      { threshold: 0.5 },
    )
    root.querySelectorAll<HTMLElement>('.stat-num[data-target]').forEach((el) => statObs.observe(el))

    // Hero stats — fade up on load with stagger
    root.querySelectorAll<HTMLElement>('.hero-stat').forEach((el, i) => {
      el.style.animation = `go-fadeUp 0.5s cubic-bezier(0.25,0.46,0.45,0.94) ${0.7 + i * 0.1}s both`
    })

    return () => {
      scrollObs.disconnect()
      statObs.disconnect()
    }
  }, [])

  const closeMenu = () => setMenuOpen(false)

  return (
    <div className="go-landing" ref={rootRef}>
      {/* ── NAV ── */}
      <nav className="nav" id="home">
        <a href="#home" className="nav-logo" onClick={closeMenu}>
          <span className="nav-logo-icon"><LogoMark /></span>
          <span className="nav-logo-text">GroundOps</span>
        </a>
        <button className="nav-menu-btn" aria-label="Open menu" onClick={() => setMenuOpen(true)}>
          <span /><span />
        </button>
      </nav>

      <div className={`nav-overlay${menuOpen ? ' open' : ''}`} id="navOverlay">
        <button className="nav-overlay-close" aria-label="Close menu" onClick={closeMenu}>✕</button>
        <nav>
          <a href="#home" onClick={closeMenu}>Home</a>
          <a href="#platform" onClick={closeMenu}>Platform</a>
          <a href="#process" onClick={closeMenu}>How it works</a>
          <a href="#pricing" onClick={closeMenu}>Pricing</a>
          <a href="#contact" onClick={closeMenu}>Contact</a>
          <Link to="/support" onClick={closeMenu}>Support</Link>
          {/* Log in targets production (#1319). /signup targets production
              too (#1367) — see SignupRedirect.tsx; when the self-serve flags
              are off there, it renders the Request Access form. */}
          <a href="https://app.groundops.app/login" onClick={closeMenu}>Log in</a>
        </nav>
      </div>

      {/* ── 1. HERO ── */}
      <header className="hero">
        <div className="hero-grid-lines" aria-hidden />
        <div className="hero-dot-field" aria-hidden />

        {/* Hero workflow diagram (desktop) */}
        <div className="hero-diagram" aria-hidden>
          <svg className="hero-diagram-lines" viewBox="0 0 300 300" fill="none">
            <line x1="150" y1="60" x2="150" y2="120" stroke="#c0b8b2" strokeWidth="1.5" className="hero-diagram-arrow" />
            <line x1="150" y1="180" x2="150" y2="240" stroke="#c0b8b2" strokeWidth="1.5" className="hero-diagram-arrow" />
            <line x1="210" y1="150" x2="265" y2="150" stroke="#c0b8b2" strokeWidth="1.5" className="hero-diagram-arrow" />
            <line x1="35" y1="150" x2="90" y2="150" stroke="#c0b8b2" strokeWidth="1.5" className="hero-diagram-arrow" />
          </svg>

          <div className="hero-diagram-center">
            <svg viewBox="0 0 51 51" className="hero-diagram-icon">
              <rect width="51" height="51" fill="var(--accent)" />
              {[10, 20, 30, 40, 51].map((v) => (
                <line key={`a${v}`} x1="0" y1={v} x2={v} y2="0" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
              ))}
              {[10, 20, 30, 40].map((v) => (
                <line key={`b${v}`} x1={v} y1="51" x2="51" y2={v} stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
              ))}
              <path d="M4 12 L4 4 L12 4" fill="none" stroke="white" strokeWidth="2" />
              <path d="M39 4 L47 4 L47 12" fill="none" stroke="white" strokeWidth="2" />
              <path d="M47 39 L47 47 L39 47" fill="none" stroke="white" strokeWidth="2" />
              <path d="M12 47 L4 47 L4 39" fill="none" stroke="white" strokeWidth="2" />
              <g transform="translate(15.5 15.5) scale(0.625)">
                <path d="M17.372 2C14.9129 2 12.495 2.61462 10.3495 3.78506C8.20402 4.9555 6.4027 6.64261 5.11785 8.68502C3.83301 10.7274 3.10762 13.0568 3.01108 15.4504C2.91454 17.8439 3.45007 20.2216 4.56645 22.3559C5.68284 24.4902 7.34272 26.3098 9.38734 27.6406C11.432 28.9714 13.7929 29.7689 16.2444 29.9568C18.6958 30.1448 21.1558 29.7169 23.389 28.714C25.6221 27.7111 27.5538 26.1669 28.9992 24.229L23.4707 20.3163C22.7126 21.3328 21.6994 22.1428 20.528 22.6688C19.3567 23.1948 18.0664 23.4193 16.7805 23.3207C15.4947 23.2221 14.2563 22.8038 13.1838 22.1058C12.1114 21.4077 11.2407 20.4533 10.6552 19.3338C10.0696 18.2143 9.78871 16.9672 9.83935 15.7117C9.88998 14.4562 10.2705 13.2344 10.9444 12.1631C11.6183 11.0918 12.5632 10.2069 13.6885 9.59297C14.8139 8.97904 16.0822 8.65666 17.372 8.65666V2Z" fill="white" />
                <path d="M29 18.2791C28.186 18.3956 27.3919 18.6285 26.6443 18.9734C25.6914 19.4129 24.8347 20.0242 24.1176 20.7727L19.214 17.3018C20.5424 15.7634 22.1946 14.52 24.0611 13.6591C25.6175 12.9412 27.2906 12.5031 29 12.3622V18.2791Z" fill="white" />
              </g>
            </svg>
          </div>

          {/* Top card — camera */}
          <div className="hero-diagram-card-top">
            <div className="diagram-card">
              <div className="diagram-card-icon accent-dot">
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <rect x="3" y="7" width="18" height="13" rx="1" fill="none" stroke="var(--accent)" strokeWidth="1.5" />
                  <path d="M8 7l1.5-2.5h5L16 7" fill="none" stroke="var(--accent)" strokeWidth="1.5" />
                  <circle cx="12" cy="13.5" r="3" fill="none" stroke="var(--accent)" strokeWidth="1.5" />
                </svg>
              </div>
            </div>
          </div>

          {/* Bottom card — report */}
          <div className="hero-diagram-card-bottom">
            <div className="diagram-card">
              <div className="diagram-card-icon accent-dot">
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <rect x="4" y="2" width="16" height="20" rx="1" fill="none" stroke="var(--accent)" strokeWidth="1.5" />
                  <line x1="8" y1="10" x2="16" y2="10" stroke="var(--accent)" strokeWidth="1.5" />
                  <line x1="8" y1="14" x2="13" y2="14" stroke="var(--accent)" strokeWidth="1.5" />
                </svg>
              </div>
            </div>
          </div>

          {/* Right card — crew/stack */}
          <div className="hero-diagram-card-right">
            <div className="diagram-card small">
              <div className="diagram-bar" />
              <div className="diagram-bar" />
            </div>
          </div>
        </div>

        <div className="hero-content">
          <h1 className="hero-title">Document field work<br />with verified photos</h1>
          <p className="hero-sub">
            GROUNDOPS HELPS CIVIL CREWS CAPTURE,<br />
            VERIFY AND REPORT EVERY INSTALLATION —<br />
            FROM THE PIT TO THE OFFICE
          </p>
          <div className="hero-btns">
            <a href="/signup" className="btn btn-solid">Get started free</a>
            <a href="#contact" className="btn btn-outline">Book a demo</a>
          </div>
        </div>
      </header>

      {/* Hero stats strip */}
      <div className="hero-stats">
        <div className="hero-stat">
          <div className="hero-stat-num">GPS</div>
          <div className="hero-stat-label">+ timestamp on<br />every photo</div>
        </div>
        <div className="hero-stat">
          <div className="hero-stat-num">Offline</div>
          <div className="hero-stat-label">capture works<br />without signal</div>
        </div>
        <div className="hero-stat">
          <div className="hero-stat-num">Live</div>
          <div className="hero-stat-label">job status from<br />field to office</div>
        </div>
      </div>

      {/* ── 2. MARQUEE — capability strip ── */}
      <div className="marquee-wrap">
        <div className="marquee-track">
          <div className="marquee-inner">
            {[...MARQUEE, ...MARQUEE].map((m, i) => (
              <span className="marquee-item" key={i}>
                {m.icon}
                {m.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── 3. PLATFORM / WHAT GROUNDOPS DOES ── */}
      <section className="section section-white" id="platform">
        <div className="container">
          <span className="eyebrow">// The platform</span>
          <h2 className="section-title">Everything your<br />crew needs</h2>
          <div className="grid-wrap cols-2 cols-3">
            {CAPABILITIES.map((c) => (
              <div className="grid-cell" key={c.n}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 56 }}>
                  <span className="card-eyebrow">{c.n}</span>
                  <CornerBracket />
                </div>
                <h3 className="card-title">{c.title}</h3>
                <p className="card-label">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full-width dark CTA band */}
      <section className="section-dark">
        <div className="container" style={{ paddingTop: 56, paddingBottom: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
          <h2 className="section-title" style={{ marginBottom: 0, maxWidth: 640 }}>
            Put compliance documentation in every crew&rsquo;s pocket.
          </h2>
          <a href="#contact" className="btn btn-accent btn-full" style={{ width: 'auto', display: 'inline-block' }}>Book a demo</a>
        </div>
      </section>

      {/* ── 4. HOW IT WORKS ── */}
      <section className="section section-light" id="process">
        <div className="container">
          <span className="eyebrow">// How it works</span>
          <h2 className="section-title">From site to<br />submission in four steps</h2>
          <div className="process-list">
            {PROCESS.map((s) => (
              <div className="process-step" key={s.n}>
                <span className="step-num">{s.n}</span>
                <div className="step-body">
                  <h3 className="step-title">{s.title}</h3>
                  <p className="step-label">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. RESULTS / STATS ── */}
      <section className="section section-white">
        <div className="container">
          <span className="eyebrow">// The results</span>
          <h2 className="section-title">Documentation that<br />pays for itself</h2>
          <div className="stats-list">
            {STATS.map((s) => (
              <div className="stat-row" key={s.label}>
                <span className="stat-num" data-target={s.target} data-suffix={s.suffix}>
                  {s.target}{s.suffix}
                </span>
                <span className="stat-label">
                  {s.label.split('\n').map((line, i) => (
                    <span key={i}>{line}<br /></span>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. WORK TYPES (carousel) ── */}
      <section className="section section-dark" id="work">
        <div className="container">
          <span className="eyebrow">// In the field</span>
          <h2 className="section-title">Built for the<br />work you do</h2>
          <div className="carousel-nav">
            <button className="carousel-btn" aria-label="Previous" onClick={() => setWorkIdx((i) => (i - 1 + WORK_TYPES.length) % WORK_TYPES.length)}>‹</button>
            <button className="carousel-btn" aria-label="Next" onClick={() => setWorkIdx((i) => (i + 1) % WORK_TYPES.length)}>›</button>
          </div>
          <div className="carousel">
            <div className="carousel-track" style={{ transform: `translateX(-${workIdx * 100}%)` }}>
              {WORK_TYPES.map((w) => (
                <div className="carousel-slide" key={w.title}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 320px) 1fr', gap: 40, alignItems: 'center' }}>
                    <div className={`halftone-box ${w.tone}`} />
                    <div>
                      <h3 className="testi-name case-name" style={{ color: 'var(--text-light)' }}>{w.title}</h3>
                      <p className="case-desc" style={{ opacity: 0.75, lineHeight: 1.7, maxWidth: 460, marginBottom: 28 }}>{w.desc}</p>
                      <a href="#contact" className="btn btn-accent">See it in action</a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 7. INDUSTRIES ── */}
      <section className="section section-white" id="industries">
        <div className="container">
          <span className="eyebrow">// Who it&rsquo;s for</span>
          <h2 className="section-title">Made for civil<br />and infrastructure</h2>
          <div className="grid-wrap cols-2 cols-3">
            {INDUSTRIES.map((s) => (
              <div className="grid-cell solution-card" key={s.title}>
                <span className="card-eyebrow">{s.eyebrow}</span>
                <h3 className="card-title" style={{ marginTop: 12 }}>{s.title}</h3>
                <p className="card-label">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 8. INTEGRATIONS HUB ── */}
      <section className="section section-light" id="integrations">
        <div className="container" style={{ textAlign: 'center' }}>
          <span className="eyebrow">// Connects to your workflow</span>
          <h2 className="section-title" style={{ textAlign: 'center' }}>Fits the tools<br />you already use</h2>
          <div className="hub-wrap">
            <div className="hub-center"><LogoMark size={22} /></div>
            {HUB_NODES.map((label, i) => (
              <div className={`hub-node hub-node-${i + 1}`} key={label}>{label}</div>
            ))}
          </div>
          <p className="hub-sub">
            Approved photos push straight to Google Drive · activity alerts<br />
            land in Slack and email · everything syncs to secure cloud storage
          </p>
        </div>
      </section>

      {/* ── 9. WHY CHOOSE US ── */}
      <section className="section section-light">
        <div className="container">
          <span className="eyebrow">// Why GroundOps</span>
          <h2 className="section-title">Why crews<br />choose us</h2>
          <div className="grid-wrap cols-2 cols-4">
            {WHY.map((w) => (
              <div className="grid-cell why-card" key={w.n}>
                <span className="why-num">{w.n}</span>
                <div>
                  <h3 className="card-title">{w.title}</h3>
                  <p className="card-label">{w.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 10. TESTIMONIALS (carousel) — PLACEHOLDER content ── */}
      <section className="section section-mid">
        <div className="container">
          <span className="eyebrow">// From the field</span>
          <div className="carousel-nav">
            <button className="carousel-btn" aria-label="Previous" onClick={() => setTestiIdx((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}>‹</button>
            <button className="carousel-btn" aria-label="Next" onClick={() => setTestiIdx((i) => (i + 1) % TESTIMONIALS.length)}>›</button>
          </div>
          <div className="carousel">
            <div className="carousel-track" style={{ transform: `translateX(-${testiIdx * 100}%)` }}>
              {TESTIMONIALS.map((t, i) => (
                <div className="carousel-slide" key={i}>
                  <p className="testi-quote">&ldquo;{t.quote}&rdquo;</p>
                  <div className="testi-name">{t.name}</div>
                  <div className="testi-role">{t.role}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rating-row">
            {/* PLACEHOLDER — no public rating yet. Replace or remove before launch. */}
            <span className="rating-label">Customer<br />feedback</span>
            <span className="rating-label" style={{ opacity: 0.8 }}>
              Real crew quotes coming soon — these are<br />illustrative samples of the kind of feedback we hear.
            </span>
          </div>
        </div>
      </section>

      {/* ── 11. PRICING — early access ── */}
      <section className="section section-dark" id="pricing">
        <div className="container">
          <span className="eyebrow">// Pricing</span>
          <h2 className="section-title">Early access</h2>
          <div className="pricing-tier">
            <div>
              <div className="tier-name">Founding teams</div>
              <div className="tier-desc" style={{ maxWidth: 520 }}>
                GroundOps is free while it&rsquo;s in early access — get started
                today, no credit card required. Pricing will be
                simple and per-seat, shaped with our founding teams, with plenty of
                notice before it starts. Prefer a walkthrough first? Book a demo.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <a href="/signup" className="btn btn-accent">Get started free</a>
              <a href="#contact" className="btn btn-outline">Talk to us</a>
            </div>
          </div>
        </div>
      </section>

      {/* ── 12. FAQ ── */}
      <section className="section section-white" id="faq">
        <div className="container">
          <span className="eyebrow">// FAQ</span>
          <h2 className="section-title">Questions,<br />answered</h2>
          <div className="faq-list">
            {FAQ.map((f, i) => (
              <div className={`faq-item${faqOpen === i ? ' open' : ''}`} key={i}>
                <button className="faq-q" onClick={() => setFaqOpen(faqOpen === i ? null : i)} aria-expanded={faqOpen === i}>
                  <span className="faq-q-text">{f.q}</span>
                  <span className="faq-toggle">+</span>
                </button>
                {/* Grid-rows wrapper: 0fr→1fr animates open to content height */}
                <div className="faq-answer"><p className="faq-answer-inner">{f.a}</p></div>
              </div>
            ))}
          </div>
          <div className="faq-footer">
            <span className="faq-footer-text">STILL HAVE QUESTIONS?<br />WE&rsquo;RE HAPPY TO HELP.</span>
            <a href="#contact" className="btn btn-solid" style={{ flex: 'none' }}>Talk to us</a>
          </div>
        </div>
      </section>

      {/* ── 13. FOOTER ── */}
      <footer id="contact">
        <div className="footer-inner">
          <div className="footer-topbar">
            <a href="mailto:hello@groundops.app" className="footer-email">hello@groundops.app</a>
          </div>

          <h2 className="footer-big-text">Document field operations<br />with confidence.</h2>

          <p className="form-label" style={{ fontSize: 13, marginTop: 24 }}>
            Ready to go?{' '}
            <a href="/signup" style={{ textDecoration: 'underline' }}>
              Get started
            </a>{' '}
            — free during early access. Or get a demo first:
          </p>

          <div className="footer-form">
            {demoStatus === 'sent' ? (
              <p className="form-label" style={{ fontSize: 14, lineHeight: 1.6 }}>
                Thanks — your demo request is in. We&rsquo;ll be in touch shortly (check your
                inbox for a confirmation).
              </p>
            ) : (
              <>
                <label className="form-label" htmlFor="footer-email-input">Get a demo — leave your details</label>
                <input
                  id="footer-email-input"
                  type="email"
                  placeholder="you@company.com"
                  value={demoEmail}
                  onChange={(e) => setDemoEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitDemoRequest() }}
                />
                {/* Optional lead context — the API already forwards both to the
                    inquiries inbox + Slack ping, so a lead arrives ready to
                    reply to instead of as a bare address. */}
                <input
                  type="text"
                  placeholder="Your name (optional)"
                  autoComplete="name"
                  maxLength={100}
                  value={demoName}
                  onChange={(e) => setDemoName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitDemoRequest() }}
                />
                <input
                  type="text"
                  placeholder="Company (optional)"
                  autoComplete="organization"
                  maxLength={150}
                  value={demoCompany}
                  onChange={(e) => setDemoCompany(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitDemoRequest() }}
                />
                {/* Honeypot — visually hidden; bots that fill it get a fake success */}
                <input
                  type="text"
                  value={demoTrap}
                  onChange={(e) => setDemoTrap(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: 'absolute', left: -9999, height: 0, opacity: 0 }}
                />
                <button
                  type="button"
                  onClick={submitDemoRequest}
                  disabled={demoStatus === 'sending' || !demoEmail.trim()}
                  className="btn btn-solid btn-full"
                >
                  {demoStatus === 'sending' ? 'Sending…' : 'Request a demo'}
                </button>
                {demoStatus === 'error' && (
                  <p className="form-label" style={{ fontSize: 13 }}>
                    {demoError ?? (
                      <>
                        Something went wrong — email us at{' '}
                        <a href="mailto:hello@groundops.app" style={{ textDecoration: 'underline' }}>hello@groundops.app</a>
                      </>
                    )}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="footer-nav">
            <a href="#home">Home</a>
            <a href="#platform">Platform</a>
            <a href="#process">How it works</a>
            <a href="#industries">Industries</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
            <Link to="/support">Support</Link>
            <Link to="/privacy">Privacy</Link>
          </div>

          <div className="footer-bottom">
            <div className="footer-logo">
              <span className="footer-logo-icon"><LogoMark /></span>
              <span className="footer-logo-name">GroundOps</span>
            </div>
            <span className="footer-built">© 2026 GroundOps Tech Pty Ltd · All rights reserved</span>
          </div>
        </div>
      </footer>
    </div>
  )
}

/* ── Marquee data (defined after component for readability) ──── */
const MARQUEE: { label: string; icon: JSX.Element }[] = [
  { label: 'Photo checklists', icon: <svg width="16" height="16" viewBox="0 0 16 16"><rect x="2" y="2" width="12" height="12" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M5 8l2 2 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg> },
  { label: 'Office review', icon: <svg width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" /><circle cx="8" cy="8" r="2" fill="currentColor" /></svg> },
  { label: 'GPS tagging', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M8 1c2.8 0 5 2.2 5 5 0 3.5-5 9-5 9S3 9.5 3 6c0-2.8 2.2-5 5-5Z" fill="none" stroke="currentColor" strokeWidth="1.5" /><circle cx="8" cy="6" r="1.6" fill="currentColor" /></svg> },
  { label: 'Offline capture', icon: <svg width="16" height="16" viewBox="0 0 16 16"><path d="M2 8a6 6 0 0 1 12 0" fill="none" stroke="currentColor" strokeWidth="1.5" /><line x1="8" y1="8" x2="8" y2="14" stroke="currentColor" strokeWidth="1.5" /></svg> },
  { label: 'Compliance reports', icon: <svg width="16" height="16" viewBox="0 0 16 16"><rect x="3" y="1" width="10" height="14" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5" /><line x1="6" y1="6" x2="10" y2="6" stroke="currentColor" strokeWidth="1.5" /><line x1="6" y1="9" x2="10" y2="9" stroke="currentColor" strokeWidth="1.5" /></svg> },
]
